import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Option } from '../../../models/option.model';
import { PartDetails } from '../../../models/part-details.model';
import { PartsAndInventoryService } from '../../../services/parts-and-inventory.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { AddStockRequest } from '../../../models/add-stock-request.model';
import { PartSupplier } from '../../../models/part-supplier.model';
import { AuthService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';

@Component({
  selector: 'app-add-stock',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, FlatpickrDatePickerComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './add-stock.component.html',
  styleUrl: './add-stock.component.scss'
})
export class AddStockComponent {
  @Input() partId!: number;

  form: FormGroup;
  isLoading = false;
  isPartLoading = false;
  isSuppliersLoading = false;
  currencyCode: string = '';
  currencySymbol: string = '';

  supplierOptions: Option[] = [];

  private transloco = inject(TranslocoService);

  warrantyValueOptions: Option[] = [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
  ];

  warrantyUnitOptions: Option[] = [
    { label: this.transloco.translate('parts.modals.addStock.warrantyUnitMonths'), value: 'Months' },
    { label: this.transloco.translate('parts.modals.addStock.warrantyUnitYears'), value: 'Years' },
  ];

  private partDetails: PartDetails | null = null;

  private partsAndInventoryService = inject(PartsAndInventoryService);
  private errorService = inject(ErrorHandlerService);
  private activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);
  private authService: AuthService = inject(AuthService);
  private fb = inject(FormBuilder);

  constructor() {
    this.form = this.fb.group({
      partName: [{ value: '', disabled: true }, Validators.required],
      partNumber: [{ value: '', disabled: true }, Validators.required],
      description: [{ value: '', disabled: true }],
      date: ['', Validators.required],
      quantityReceived: ['', Validators.required],
      unitCost: ['', Validators.required],
      invoiceNumber: [''],
      supplier: ['', Validators.required],
      warrantyValue: ['', Validators.required],
      warrantyUnit: ['', Validators.required],
      leadTime: ['', [Validators.required, Validators.pattern('^[0-9]*$')]]
    });

    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  get modalTitle(): string {
    const partNumber = this.partDetails?.partNumber ?? '';
    return partNumber
      ? this.transloco.translate('parts.modals.addStock.titleForPart', { partNumber })
      : this.transloco.translate('parts.modals.addStock.title');
  }

  get supplierLabel(): string {
    if (this.isSuppliersLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingSuppliers');
    }

    const selected = String(this.form.get('supplier')?.value ?? '');
    return this.supplierOptions.find(option => option.value === selected)?.label || this.transloco.translate('parts.modals.dropdown.selectSupplier');
  }

  get warrantyUnitLabel(): string {
    const unit = String(this.form.get('warrantyUnit')?.value ?? '');
    return this.warrantyUnitOptions.find(option => option.value === unit)?.label
      ?? this.transloco.translate('parts.modals.addStock.warrantyUnitYears');
  }

  onSelect(field: string, value: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.form.get(field)?.setValue(value);
    this.form.get(field)?.markAsTouched();
  }

  onDateChange(value: string | undefined): void {
    this.form.get('date')?.setValue(value ?? '');
    this.form.get('date')?.markAsTouched();
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched());
      return;
    }

    this.isLoading = true;
    try {
      const request = this.buildAddStockRequest();
      await this.partsAndInventoryService.addPartStockAsync(request);
      this.toastr.success(this.transloco.translate('parts.modals.addStock.toastAdded'));
      this.activeModal.close(true);
    } catch (error: any) {
      this.errorService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private async initializeForm(): Promise<void> {
    this.form.patchValue({
      partName: this.transloco.translate('parts.modals.placeholders.loadingPartName'),
      partNumber: '',
      description: '',
      date: this.getTodayIsoDate(),
      quantityReceived: '',
      unitCost: '',
      invoiceNumber: '',
      supplier: '',
      warrantyValue: '',
      warrantyUnit: '',
      leadTime: ''
    });

    if (!Number.isFinite(this.partId)) {
      this.errorService.handleError('Invalid part id');
      this.onCancel();
      return;
    }

    await Promise.all([
      this.loadPartDetails(),
      this.loadSuppliers()
    ]);

    this.form.patchValue({
      partName: this.partDetails?.partName ?? '',
      partNumber: this.partDetails?.partNumber ?? '',
      description: this.partDetails?.description ?? ''
    });
  }

  private async loadPartDetails(): Promise<void> {
    this.isPartLoading = true;
    try {
      const response = await this.partsAndInventoryService.getPartByIdAsync(this.partId);
      this.partDetails = response.data ?? null;
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isPartLoading = false;
    }
  }

  private getTodayIsoDate(): string {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private async loadSuppliers(): Promise<void> {
    this.isSuppliersLoading = true;
    try {
      const response = await this.partsAndInventoryService.getPartSuppliersAsync(this.partId);
      this.supplierOptions = (response.data ?? []).map((supplier: PartSupplier) => ({
        label: supplier.name,
        value: String(supplier.id)
      }));

      const currentSupplier = String(this.form.get('supplier')?.value ?? '');
      const isCurrentSupplierValid = this.supplierOptions.some(option => option.value === currentSupplier);
      if (!isCurrentSupplierValid) {
        this.form.get('supplier')?.setValue('');
      }
    } catch (error) {
      this.supplierOptions = [];
      this.form.get('supplier')?.setValue('');
      this.errorService.handleError(error);
    } finally {
      this.isSuppliersLoading = false;
    }
  }

  private buildAddStockRequest(): AddStockRequest {
    const quantityReceived = Number(this.form.get('quantityReceived')?.value ?? 0);
    const unitCost = Number(this.form.get('unitCost')?.value ?? 0);
    const supplierId = Number(this.form.get('supplier')?.value ?? 0);

    return {
      partId: this.partId,
      dateReceived: String(this.form.get('date')?.value ?? ''),
      quantityReceived,
      unitCost,
      invoiceNumber: this.toNullableString(this.form.get('invoiceNumber')?.value),
      supplierId,
      warranty: this.computeWarrantyMonths(),
      leadTime: this.toNullableString(this.form.get('leadTime')?.value)
    };
  }

  private computeWarrantyMonths(): number | null {
    const val = Number(this.form.get('warrantyValue')?.value);
    const unit = String(this.form.get('warrantyUnit')?.value ?? '');
    if (!Number.isFinite(val) || val <= 0) return null;
    return unit.toLowerCase().includes('year') ? val * 12 : val;
  }

  private toNullableString(value: unknown): string | null {
    const parsed = String(value ?? '').trim();
    return parsed.length ? parsed : null;
  }
}